"use client";

import type { Node } from "@tiptap/pm/model";
import type { Editor } from "@tiptap/react";
import { createContext, useContext } from "react";
import type { SuggestionItem } from "@/types/suggestions";
import type { EmailBlockDefinition } from "./blocks/types";
import type { EmailStyleSettings } from "./style-settings";
export type { EmailStyleSettings } from "./style-settings";
export { DEFAULT_STYLE_SETTINGS, getBodyStyles, getContainerStyles } from "./style-settings";
export interface ActiveBlock {
  node: Node;
  pos: number;
  definition: EmailBlockDefinition;
}
export type EmailComposerMode = "simple" | "block";
export interface EmailComposerContextValue {
  /** Interaction mode: "simple" = click-to-edit, "block" = click-to-select row */
  mode: EmailComposerMode;
  /** The Tiptap editor instance */
  editor: Editor | null;
  /** The full block registry */
  blockRegistry: Record<string, EmailBlockDefinition>;
  /** Currently selected block (for properties panel) */
  activeBlock: ActiveBlock | null;
  /** Set the active block (for properties panel) */
  setActiveBlock: (block: ActiveBlock | null) => void;
  /** Whether the user clicked the background (not a block) */
  backgroundSelected: boolean;
  /** Set backgroundSelected */
  setBackgroundSelected: (selected: boolean) => void;
  /** Style settings (merged defaults + consumer overrides) */
  styleSettings: EmailStyleSettings;
  /** Update style settings */
  setStyleSettings?: (settings: Partial<EmailStyleSettings> | ((prev: EmailStyleSettings) => Partial<EmailStyleSettings>)) => void;
  /** Available template variables */
  variables: SuggestionItem[];
  /**
   * Whether picking a variable inserts its resolved text instead of a mention
   * placeholder. Mirrors `EmailComposerProps.resolveOnInsert` so the "Insert
   * variable" toolbar button inserts exactly what `@`-typing does.
   */
  resolveOnInsert: boolean;
  /** Image upload handler (null = media blocks disabled in palette) */
  onImageUpload: ((file: File) => Promise<string>) | null;
  /** File attachment handler (null = attachment features disabled) */
  onAddFiles: ((files: File[]) => {
    added: number;
    errors: string[];
  }) | null;
  /** AI generation callback (null = AI button hidden) */
  onAI: (() => void) | null;
  /**
   * Optional async alt-text generator for images. Receives the image `src`
   * (typically a blob: URL set at upload time) and resolves to alt text.
   * When provided, the image bubble menu surfaces an AI button that calls
   * this and writes the result back onto the node's `alt` attribute.
   */
  onGenerateAltText?: ((src: string) => Promise<string>) | null;
  /** Container ref for bubble menus */
  bubbleMenuContainerRef: React.RefObject<HTMLDivElement | null>;
}
export const EmailComposerContext = createContext<EmailComposerContextValue | null>(null);

/**
 * Hook to access the email composer context.
 * Must be used within an EmailComposer provider.
 * Throws if used outside of EmailComposer.
 */
export function useEmailComposer() {
  const context = useContext(EmailComposerContext);
  if (!context) {
    throw new Error("useEmailComposer must be used within an <EmailComposer> provider");
  }
  return context;
}